import type { Patrol } from '@local/common-lib';
import {
  combine,
  formatDurationBetween,
  formatPeople,
  shortAddress,
} from '@local/common-lib';
import {
  Email as CommsIcon,
  Reply as CommsReplyIcon,
  Stop as FinishIcon,
  PlayArrow as StartIcon,
} from '@mui/icons-material';
import { TimelineDot } from '@mui/lab';
import {
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { getTimeLineDot } from './dot.js';
import { Plate } from './plate.js';

interface Props {
  patrol: Patrol.Type;
}

export const PatrolTimeTable: FC<Props> = ({ patrol }) => {
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const incidents = [...(patrol.incidents ?? [])];
  incidents.sort((a, b) => a.when.getTime() - b.when.getTime());

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell
            sx={{
              pr: { sm: 3, xs: 0 },
              whiteSpace: 'nowrap',
              width: { sm: 'fit-content', xs: '1%' },
            }}
          >
            When
          </TableCell>
          <TableCell
            sx={{
              pr: { sm: 3, xs: 0 },
              whiteSpace: 'nowrap',
              width: { sm: 'fit-content', xs: '1%' },
            }}
          >
            What
          </TableCell>
          <TableCell>Where</TableCell>
          <TableCell sx={{ display: { sm: 'table-cell', xs: 'none' } }}>
            Details
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {patrol.eccLogOnNotification ? (
          <TableRow>
            <TableCell
              sx={{
                pr: { sm: 3, xs: 0 },
                width: { sm: 'fit-content', xs: '1%' },
              }}
            >
              {DateTime.fromJSDate(
                patrol.eccLogOnNotification.when,
              ).toLocaleString(DateTime.TIME_SIMPLE)}
            </TableCell>
            <TableCell
              sx={{
                pr: { sm: 3, xs: 0 },
                width: { sm: 'fit-content', xs: '1%' },
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  whiteSpace: 'nowrap',
                }}
              >
                <TimelineDot color="info">
                  <CommsIcon />
                </TimelineDot>
                <Typography>{smallScreen ? 'Log On' : 'ECC Log On'}</Typography>
              </Stack>
            </TableCell>
            <TableCell> - </TableCell>
            <TableCell sx={{ display: { sm: 'table-cell', xs: 'none' } }}>
              {' - '}
            </TableCell>
          </TableRow>
        ) : null}
        {patrol.eccLogOnResponse ? (
          <TableRow>
            <TableCell
              sx={{
                pr: { sm: 3, xs: 0 },
                width: { sm: 'fit-content', xs: '1%' },
              }}
            >
              {DateTime.fromJSDate(patrol.eccLogOnResponse.when).toLocaleString(
                DateTime.TIME_SIMPLE,
              )}
            </TableCell>
            <TableCell
              sx={{
                pr: { sm: 3, xs: 0 },
                width: { sm: 'fit-content', xs: '1%' },
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  whiteSpace: 'nowrap',
                }}
              >
                <TimelineDot color="success">
                  <CommsReplyIcon />
                </TimelineDot>
                <Stack direction="column">
                  <Typography>ECC Reply</Typography>
                  {smallScreen && patrol.eccLogOnResponse.policeEventNumber ? (
                    <Typography color="text.secondary" variant="body2">
                      {patrol.eccLogOnResponse.policeEventNumber}
                    </Typography>
                  ) : null}
                </Stack>
              </Stack>
            </TableCell>
            <TableCell>{' - '}</TableCell>
            <TableCell sx={{ display: { sm: 'table-cell', xs: 'none' } }}>
              <Typography>
                Police #: {patrol.eccLogOnResponse.policeEventNumber}
              </Typography>
            </TableCell>
          </TableRow>
        ) : null}
        {patrol.started ? (
          <TableRow>
            <TableCell
              sx={{
                pr: { sm: 3, xs: 0 },
                width: { sm: 'fit-content', xs: '1%' },
              }}
            >
              {DateTime.fromJSDate(patrol.started.started).toLocaleString(
                DateTime.TIME_SIMPLE,
              )}
            </TableCell>
            <TableCell
              sx={{
                pr: { sm: 3, xs: 0 },
                width: { sm: 'fit-content', xs: '1%' },
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  whiteSpace: 'nowrap',
                }}
              >
                <TimelineDot color="warning">
                  <StartIcon />
                </TimelineDot>
                <Stack direction="column">
                  <Typography>Started</Typography>
                  {smallScreen && patrol.started.policeEventNumber ? (
                    <Typography color="text.secondary" variant="body2">
                      {patrol.started.policeEventNumber}
                    </Typography>
                  ) : null}
                </Stack>
              </Stack>
            </TableCell>
            <TableCell>
              {patrol.started.location ? (
                <Stack>
                  {patrol.started.location.address ? (
                    <Typography>
                      {shortAddress(patrol.started.location.address)}
                    </Typography>
                  ) : null}
                  {patrol.started.location.latitude &&
                  patrol.started.location.longitude ? (
                    <Typography
                      color="text.secondary"
                      sx={{ whiteSpace: 'nowrap' }}
                      variant="body2"
                    >
                      {patrol.started.location.latitude},{' '}
                      {patrol.started.location.longitude}
                    </Typography>
                  ) : (
                    <Typography
                      color="text.secondary"
                      fontStyle="italic"
                      sx={{ whiteSpace: 'nowrap' }}
                      variant="body2"
                    >
                      No GPS
                    </Typography>
                  )}
                  {patrol.started.location.notes ? (
                    <Typography color="text.secondary" variant="body2">
                      {patrol.started.location.notes}
                    </Typography>
                  ) : null}
                </Stack>
              ) : (
                <Typography
                  color="text.secondary"
                  fontStyle="italic"
                  variant="body2"
                >
                  Unknown
                </Typography>
              )}
            </TableCell>
            <TableCell sx={{ display: { sm: 'table-cell', xs: 'none' } }}>
              {patrol.started.policeEventNumber ? (
                <Typography>
                  Police #: {patrol.started.policeEventNumber}
                </Typography>
              ) : null}
              {patrol.started.vehicle ? (
                <Typography>Vehicle: {patrol.started.vehicle.rego}</Typography>
              ) : null}
              {patrol.started.notes ? (
                <Typography color="text.secondary" variant="body2">
                  {patrol.started.notes}
                </Typography>
              ) : null}
            </TableCell>
          </TableRow>
        ) : null}
        {incidents.map((incident) => (
          <TableRow key={incident._id}>
            <TableCell
              sx={{
                pr: { sm: 3, xs: 0 },
                width: { sm: 'fit-content', xs: '1%' },
              }}
            >
              {DateTime.fromJSDate(incident.when).toLocaleString(
                DateTime.TIME_SIMPLE,
              )}
            </TableCell>
            <TableCell
              sx={{
                pr: { sm: 3, xs: 0 },
                width: { sm: 'fit-content', xs: '1%' },
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  whiteSpace: 'nowrap',
                }}
              >
                {getTimeLineDot(incident.what)}
                <Stack>
                  <Typography sx={{ textWrap: 'balance' }}>
                    {incident.what}
                  </Typography>
                  {incident.whatDetails ? (
                    <Typography
                      color="text.secondary"
                      sx={{ textWrap: 'balance' }}
                      variant="body2"
                    >
                      {incident.whatDetails}
                    </Typography>
                  ) : null}
                </Stack>
              </Stack>
            </TableCell>
            <TableCell>
              {incident.location ? (
                <Stack>
                  {incident.location.address ? (
                    <Typography sx={{ textWrap: 'balance' }}>
                      {shortAddress(incident.location.address)}
                    </Typography>
                  ) : null}
                  <Typography
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap' }}
                    variant="body2"
                  >
                    {incident.location.latitude}, {incident.location.longitude}
                  </Typography>
                  {incident.location.notes ? (
                    <Typography
                      color="text.secondary"
                      sx={{ textWrap: 'balance' }}
                      variant="body2"
                    >
                      {incident.location.notes}
                    </Typography>
                  ) : null}
                </Stack>
              ) : (
                <Typography
                  color="text.secondary"
                  fontStyle="italic"
                  variant="body2"
                >
                  Unknown
                </Typography>
              )}
            </TableCell>
            <TableCell sx={{ display: { sm: 'table-cell', xs: 'none' } }}>
              {incident.people ? (
                <Typography>People: {formatPeople(incident.people)}</Typography>
              ) : null}
              {incident.peopleDetails ? (
                <Typography sx={{ textWrap: 'balance' }} variant="body2">
                  {incident.peopleDetails}
                </Typography>
              ) : null}
              {incident.whenFinished ? (
                <Typography color="text.secondary" variant="body2">
                  Duration:{' '}
                  {formatDurationBetween(incident.when, incident.whenFinished)}
                </Typography>
              ) : null}
              {incident.vehicles?.length
                ? incident.vehicles.map((vehicle) => (
                    <Stack
                      alignItems="center"
                      direction="row"
                      key={vehicle.rego}
                      p="2px"
                      spacing={2}
                    >
                      <Plate
                        rego={vehicle.rego}
                        stolen={!!vehicle.reportedStolen}
                      />
                      {vehicle.color ? (
                        <Typography variant="body2">
                          {combine(
                            ' ',
                            vehicle.color,
                            vehicle.make,
                            vehicle.model,
                          )}
                        </Typography>
                      ) : undefined}
                      {vehicle.reportedStolen ? (
                        <Chip color="error" label="Stolen" size="small" />
                      ) : undefined}
                    </Stack>
                  ))
                : undefined}
            </TableCell>
          </TableRow>
        ))}
        {patrol.finished ? (
          <TableRow>
            <TableCell
              sx={{
                pr: { sm: 3, xs: 0 },
                width: { sm: 'fit-content', xs: '1%' },
              }}
            >
              {DateTime.fromJSDate(patrol.finished.finished).toLocaleString(
                DateTime.TIME_SIMPLE,
              )}
            </TableCell>
            <TableCell
              sx={{
                pr: { sm: 3, xs: 0 },
                width: { sm: 'fit-content', xs: '1%' },
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  whiteSpace: 'nowrap',
                }}
              >
                <TimelineDot color="warning">
                  <FinishIcon />
                </TimelineDot>
                <Typography>Finished</Typography>
              </Stack>
            </TableCell>
            <TableCell>
              {patrol.finished.location ? (
                <Stack>
                  {patrol.finished.location.address ? (
                    <Typography>
                      {shortAddress(patrol.finished.location.address)}
                    </Typography>
                  ) : null}
                  <Typography
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap' }}
                    variant="body2"
                  >
                    {patrol.finished.location.latitude},{' '}
                    {patrol.finished.location.longitude}
                  </Typography>
                  {patrol.finished.location.notes ? (
                    <Typography color="text.secondary" variant="body2">
                      {patrol.finished.location.notes}
                    </Typography>
                  ) : null}
                </Stack>
              ) : (
                <Typography
                  color="text.secondary"
                  fontStyle="italic"
                  variant="body2"
                >
                  Unknown
                </Typography>
              )}
            </TableCell>
            <TableCell sx={{ display: { sm: 'table-cell', xs: 'none' } }}>
              <Typography>
                {patrol.started?.vehicle && patrol.finished?.vehicle ? (
                  <Typography>
                    Distance:{' '}
                    {(
                      patrol.finished?.vehicle.finishOdometer -
                      patrol.started?.vehicle.startOdometer
                    ).toFixed(1)}{' '}
                    km
                  </Typography>
                ) : null}
              </Typography>
            </TableCell>
          </TableRow>
        ) : null}
        {patrol.eccLogOffNotification ? (
          <TableRow>
            <TableCell
              sx={{
                pr: { sm: 3, xs: 0 },
                width: { sm: 'fit-content', xs: '1%' },
              }}
            >
              {DateTime.fromJSDate(
                patrol.eccLogOffNotification.when,
              ).toLocaleString(DateTime.TIME_SIMPLE)}
            </TableCell>
            <TableCell
              sx={{
                pr: { sm: 3, xs: 0 },
                width: { sm: 'fit-content', xs: '1%' },
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  whiteSpace: 'nowrap',
                }}
              >
                <TimelineDot color="info">
                  <CommsIcon />
                </TimelineDot>
                <Typography>
                  {smallScreen ? 'Log Off' : 'ECC Log Off'}
                </Typography>
              </Stack>
            </TableCell>
            <TableCell> - </TableCell>
            <TableCell sx={{ display: { sm: 'table-cell', xs: 'none' } }}>
              {' - '}
            </TableCell>
          </TableRow>
        ) : null}
        {patrol.eccLogOffResponse ? (
          <TableRow>
            <TableCell
              sx={{
                pr: { sm: 3, xs: 0 },
                width: { sm: 'fit-content', xs: '1%' },
              }}
            >
              {DateTime.fromJSDate(
                patrol.eccLogOffResponse.when,
              ).toLocaleString(DateTime.TIME_SIMPLE)}
            </TableCell>
            <TableCell
              sx={{
                pr: { sm: 3, xs: 0 },
                width: { sm: 'fit-content', xs: '1%' },
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  whiteSpace: 'nowrap',
                }}
              >
                <TimelineDot color="success">
                  <CommsReplyIcon />
                </TimelineDot>
                <Typography>ECC Reply</Typography>
              </Stack>
            </TableCell>
            <TableCell> - </TableCell>
            <TableCell sx={{ display: { sm: 'table-cell', xs: 'none' } }}>
              {' - '}
            </TableCell>
          </TableRow>
        ) : null}
      </TableBody>
    </Table>
  );
};
